import type { FillerPreviewSources } from '@/lib/preview/fillerPreviewSources';
// src/components/layouts/FillerVideoBrowseDialog.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  filterFillerVideos,
  normalizeSearchText,
  type FillerVideoListItem,
} from '@/lib/layouts/filterFillerVideos';

export interface FillerVideoTag {
  slug: string;
  name: string;
}

interface MyVideo {
  id: string;
  url: string;
  name?: string | null;
}

interface Props {
  previewSources?: FillerPreviewSources;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presets: FillerVideoListItem[];
  myVideos: MyVideo[];
  tags: FillerVideoTag[];
  /** Current playlist — selecting several videos plays them in order, looping. */
  selectedUrls: string[];
  /** Resolves the url to apply for a preset (handles the en-language variant). */
  presetUrl: (preset: FillerVideoListItem) => string;
  onConfirm: (urls: string[]) => void;
}

const PreviewCard: React.FC<{
  selected: boolean;
  /** 1-based position in the playlist; shown as a badge when selected. */
  order?: number;
  title: string;
  thumbnailUrl?: string | null;
  videoUrl: string;
  testId: string;
  onClick: () => void;
}> = ({ selected, order, title, thumbnailUrl, videoUrl, testId, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative aspect-[9/16] rounded-md overflow-hidden border-2 bg-black/40 text-left ${
        selected ? 'border-primary' : 'border-border hover:border-white/30'
      }`}
    >
      {hovered ? (
        // The video element only mounts on hover so the grid never preloads
        // a hundred videos at once.
        <video
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover pointer-events-none"
        />
      ) : thumbnailUrl ? (
        <img src={thumbnailUrl} alt={title} loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <video
          src={videoUrl}
          muted
          preload="metadata"
          className="w-full h-full object-cover pointer-events-none"
        />
      )}
      {selected && order !== undefined && (
        <span
          data-testid={`${testId}-order`}
          className="absolute top-1 right-1 h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center"
        >
          {order}
        </span>
      )}
      <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1.5 py-0.5 text-[10px] truncate text-white">
        {title}
      </span>
    </button>
  );
};

export const FillerVideoBrowseDialog: React.FC<Props> = ({
  previewSources = {},
  open,
  onOpenChange,
  presets,
  myVideos,
  tags,
  selectedUrls,
  presetUrl,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [selection, setSelection] = useState<string[]>(selectedUrls);

  // Re-seed the working selection from the config each time the dialog opens.
  useEffect(() => {
    if (open) setSelection(selectedUrls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (url: string) =>
    setSelection((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
    );

  // Only offer chips for tags actually present on at least one preset.
  const visibleTags = useMemo(() => {
    const used = new Set(presets.flatMap((p) => p.categories));
    const named = tags.filter((tag) => used.has(tag.slug));
    const namedSlugs = new Set(named.map((tag) => tag.slug));
    // Slugs on videos with no matching tag record still get a chip (fallback
    // label = slug) so filtering never silently loses a category.
    const orphans = [...used]
      .filter((slug) => !namedSlugs.has(slug))
      .map((slug) => ({ slug, name: slug }));
    return [...named, ...orphans];
  }, [presets, tags]);

  const filteredPresets = useMemo(
    () => filterFillerVideos(presets, { search, tag: activeTag ?? undefined }),
    [presets, search, activeTag],
  );

  const filteredMyVideos = useMemo(() => {
    if (activeTag) return [];
    const needle = normalizeSearchText(search.trim());
    if (!needle) return myVideos;
    return myVideos.filter((m) => normalizeSearchText(m.name ?? '').includes(needle));
  }, [myVideos, search, activeTag]);

  const chipCls = (active: boolean) =>
    `text-xs px-2.5 py-1 rounded-full border whitespace-nowrap ${
      active
        ? 'border-primary bg-primary/10 text-primary'
        : 'border-border bg-muted/30 hover:bg-muted/50'
    }`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('projectClips.postLayouts.satisfatorio.dialogTitle')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('projectClips.postLayouts.satisfatorio.searchPlaceholder')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="filler-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('projectClips.postLayouts.satisfatorio.searchPlaceholder')}
              className="pl-8"
            />
          </div>
          {visibleTags.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              <button
                type="button"
                data-testid="filler-tag-all"
                className={chipCls(activeTag === null)}
                onClick={() => setActiveTag(null)}
              >
                {t('projectClips.postLayouts.satisfatorio.allTags')}
              </button>
              {visibleTags.map((tag) => (
                <button
                  key={tag.slug}
                  type="button"
                  data-testid={`filler-tag-${tag.slug}`}
                  className={chipCls(activeTag === tag.slug)}
                  onClick={() => setActiveTag(activeTag === tag.slug ? null : tag.slug)}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          )}
          <div className="max-h-[55vh] overflow-y-auto space-y-3 pr-1">
            {filteredPresets.length === 0 && filteredMyVideos.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {t('projectClips.postLayouts.satisfatorio.noResults')}
              </p>
            )}
            {filteredPresets.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {filteredPresets.map((p) => (
                  <PreviewCard
                    key={p.id}
                    testId={`browse-filler-${p.id}`}
                    selected={selection.includes(presetUrl(p))}
                    order={selection.indexOf(presetUrl(p)) + 1 || undefined}
                    title={p.name}
                    thumbnailUrl={p.thumbnail_url}
                    videoUrl={previewSources[presetUrl(p)]?.url ?? presetUrl(p)}
                    onClick={() => toggle(presetUrl(p))}
                  />
                ))}
              </div>
            )}
            {filteredMyVideos.length > 0 && (
              <>
                <p className="text-xs font-semibold text-muted-foreground">
                  {t('projectClips.postLayouts.satisfatorio.myUploads')}
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {filteredMyVideos.map((m) => (
                    <PreviewCard
                      key={m.id}
                      testId={`browse-my-media-${m.id}`}
                      selected={selection.includes(m.url)}
                      order={selection.indexOf(m.url) + 1 || undefined}
                      title={m.name ?? ''}
                      thumbnailUrl={null}
                      videoUrl={m.url}
                      onClick={() => toggle(m.url)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 pt-1 border-t border-border">
            <p className="text-xs text-muted-foreground">
              {t('projectClips.postLayouts.satisfatorio.selectedCount', { count: selection.length })}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="filler-clear-selection"
                disabled={selection.length === 0}
                onClick={() => setSelection([])}
                className="text-xs px-3 py-1.5 rounded-md border border-border bg-muted/30 hover:bg-muted/50 disabled:opacity-50"
              >
                {t('projectClips.postLayouts.satisfatorio.clearSelection')}
              </button>
              <button
                type="button"
                data-testid="filler-confirm-selection"
                disabled={selection.length === 0}
                onClick={() => {
                  onConfirm(selection);
                  onOpenChange(false);
                }}
                className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {t('projectClips.postLayouts.satisfatorio.confirmSelection', { count: selection.length })}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FillerVideoBrowseDialog;
